<script>
/**
 * O espectro CONTÍNUO do Ditar — ocupa a largura inteira do campo de texto
 * enquanto grava, no lugar do texto/placeholder (referência: ChatGPT real,
 * "a parte de espectro é contínuo da esquerda para a direita, com
 * alternâncias reais de voz").
 *
 * [feedback do usuário, 04/09 — 2ª rodada, com screencast] "a animacao de
 * falar no input deveria ser... continuo sempre da esquerda para a direita
 * como se tivesse caminhando" — a 1ª versão tinha 48 barras de altura FIXA
 * (perfil senoidal estático) com só um pulso de opacidade decorativo; não
 * "andava" nada. Esta versão é uma JANELA DESLIZANTE de verdade: a cada
 * `INTERVALO_AMOSTRAGEM_MS`, uma nova amostra (derivada do `nivel` — RMS real
 * do microfone — no instante) entra por uma ponta e todas as outras avançam
 * uma posição, com `transition: height` suavizando o movimento. O array
 * inteiro é reatribuído (não mutado in-place) para o Vue 2 detectar a
 * mudança sem precisar de `$set`.
 *
 * Renderizado INLINE (não `position: absolute`) — ocupa o espaço real do
 * campo, então o layout do composer não precisa reservar altura extra pra
 * ele.
 */

// [feedback do usuário, 04/09 — 4ª rodada] "as linhas muito espacadas" — 48
// barras com `justify-content: space-between` (ver o `<style>`) deixava um
// vão grande entre cada uma nos ~700-900px do composer. Mais barras =
// vãos menores para a MESMA largura, sem depender de nenhuma crescer.
const QUANTIDADE_DE_BARRAS = 100
const ALTURA_MINIMA_PX = 3
const ALTURA_MAXIMA_PX = 22

// Ritmo do "caminhar" — dissociado da taxa real de amostragem do RMS
// (`criarMedidorDeNivel`, via rAF a ~60fps): amostrar a cada frame faria a
// janela inteira (48 posições) desfilar em menos de 1s, rápido e picotado
// demais. 70ms dá um ciclo de ~3,4s pela janela inteira — passo visível,
// sem virar um blur.
const INTERVALO_AMOSTRAGEM_MS = 70

// [feedback do usuário, 04/09] Mesmo ganho já validado ao vivo no orb
// anterior — fala normal fica bem abaixo de 1 de RMS (o limiar de silêncio
// do VAD é 0.02), então precisa de amplificação pra produzir movimento
// visível.
const FATOR_DE_GANHO = 6

export default {
  name: 'IntelligenceV2ComposerDitarEspectro',
  props: {
    // RMS do microfone (0-1) — real, amostrado por `criarMedidorDeNivel`.
    nivel: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      // Janela deslizante: a posição N sempre desenha a MESMA barra no DOM
      // (mesma `key`), só o VALOR muda a cada `avancar()` — é a transição de
      // `height` nessa troca de valor que produz o efeito de andar.
      alturas: new Array(QUANTIDADE_DE_BARRAS).fill(ALTURA_MINIMA_PX),
    }
  },
  mounted() {
    this._temporizador = setInterval(this.avancar, INTERVALO_AMOSTRAGEM_MS)
  },
  beforeDestroy() {
    clearInterval(this._temporizador)
  },
  methods: {
    avancar() {
      const amplificado = Math.min(this.nivel * FATOR_DE_GANHO, 1)
      const altura =
        ALTURA_MINIMA_PX + amplificado * (ALTURA_MAXIMA_PX - ALTURA_MINIMA_PX)
      this.alturas = this.alturas.slice(1).concat(altura)
    },
  },
}
</script>

<template>
  <div
    class="composer-ditar-espectro"
    data-teste="composer-ditar-espectro"
    aria-hidden="true"
  >
    <span
      v-for="(altura, indice) in alturas"
      :key="indice"
      class="composer-ditar-espectro__barra"
      :style="{ height: altura + 'px' }"
    />
  </div>
</template>

<style lang="sass" scoped>
.composer-ditar-espectro
  display: flex
  align-items: center
  // [bug corrigido, 04/09 — 3ª rodada] Cada barra tem `max-width: 4px`
  // (abaixo), então com `justify-content: flex-start` elas se acumulavam
  // coladas à esquerda e paravam bem antes da borda direita do campo —
  // "parece ficar na metade". `space-between` distribui as 48 barras (largura
  // fixa cada) por TODA a largura disponível, sem depender de nenhuma delas
  // crescer.
  justify-content: space-between
  width: 100%
  height: 24px
  overflow: hidden
  pointer-events: none

.composer-ditar-espectro__barra
  flex: 1 1 0
  min-width: 1.5px
  max-width: 3px
  border-radius: 2px
  background: linear-gradient(to top, var(--neutral-300), var(--neutral-400))
  // A troca de VALOR de `height` a cada `avancar()` (mesmo elemento, key
  // estável) é o que produz o "caminhar" — sem esta transição, o array
  // deslizando trocaria os números de uma vez, sem suavização nenhuma.
  transition: height 0.12s linear

@media (prefers-reduced-motion: reduce)
  .composer-ditar-espectro__barra
    transition: none
</style>
